import React, { useEffect, useId, useRef, useState } from 'react';
import DsButton from '../../common/DsButton';
import PersonAvatar from '../../common/avatar/PersonAvatar';
import { buildMentionInsertion, buildOutgoingMentions, findMentionTrigger } from '../../common/chat/mentions/chatMentionUtils';
import { listTaskMentionCandidates } from '../../../api/taskProjectDetail';
import '../../../css/admin/tasks/TaskCommentComposer.css';

type Candidate = { type: string; role: string; targetId: string; label: string; profilePicture?: string | null };
type Props = { projectId: string; value: string; onChange: (value: string) => void;
  onSubmit: (metadata: any) => unknown; disabled?: boolean; readOnly?: boolean };
export default function TaskCommentComposer({ projectId, value, onChange, onSubmit, disabled = false, readOnly = false }: Props) {
  const input = useRef<HTMLTextAreaElement>(null);
  const listId = useId();
  const [trigger, setTrigger] = useState<any>(null);
  const [selected, setSelected] = useState<Candidate[]>([]);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [cursor, setCursor] = useState<number | null>(null);
  const [active, setActive] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const requestGeneration = useRef(0);
  const submission = useRef<{ fingerprint: string; id: string } | null>(null);
  useEffect(() => { if (!value) { setSelected([]); setTrigger(null); submission.current = null; } }, [value]);
  const query = trigger?.query;
  useEffect(() => {
    const generation = ++requestGeneration.current;
    setCandidates([]); setCursor(null); setActive(-1); setError('');
    if (query == null || !projectId || readOnly) { setLoading(false); return; }
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const result = await listTaskMentionCandidates(projectId, query);
        if (generation !== requestGeneration.current) return;
        setCandidates(result.candidates); setCursor(result.nextCursor ?? null);
      } catch { if (generation === requestGeneration.current) setError('候補を取得できませんでした。'); }
      finally { if (generation === requestGeneration.current) setLoading(false); }
    }, 250);
    return () => { clearTimeout(timer); requestGeneration.current++; };
  }, [query, projectId, readOnly, retry]);
  const loadMore = async () => {
    if (cursor == null || loading) return;
    const generation = requestGeneration.current;
    setLoading(true);
    try {
      const result = await listTaskMentionCandidates(projectId, query, cursor);
      if (generation !== requestGeneration.current) return;
      setCandidates((prev) => [...prev, ...result.candidates]); setCursor(result.nextCursor ?? null);
    } catch { if (generation === requestGeneration.current) setError('候補を取得できませんでした。'); }
    finally { if (generation === requestGeneration.current) setLoading(false); }
  };
  const choose = (candidate: Candidate) => {
    const insertion = buildMentionInsertion({ text: value, mentionState: trigger, label: candidate.label });
    if (!insertion) return;
    setSelected((prev) => [...prev.filter((m) => m.targetId !== candidate.targetId && m.label !== candidate.label), candidate]);
    onChange(insertion.text); setTrigger(null);
    requestAnimationFrame(() => { input.current?.focus(); input.current?.setSelectionRange(insertion.selection.start, insertion.selection.end); });
  };
  const submit = () => {
    if (disabled || readOnly || !value.trim()) return;
    const mentions = buildOutgoingMentions(selected, value.trim());
    const fingerprint = JSON.stringify([value.trim(), mentions]);
    if (submission.current?.fingerprint !== fingerprint) submission.current = { fingerprint, id: crypto.randomUUID() };
    void onSubmit({ mentions, clientCommentId: submission.current.id });
  };
  return <div className="task-comment-composer">
    <textarea ref={input} rows={3} value={value} placeholder="コメントを追加（@でメンション）"
      disabled={disabled || readOnly} maxLength={10000}
      onChange={(event) => {
        const next = event.target.value;
        setSelected((prev) => prev.filter((m) => buildOutgoingMentions([m], next).length));
        onChange(next); setTrigger(findMentionTrigger(next, event.target.selectionStart));
      }}
      onSelect={(event) => setTrigger(findMentionTrigger(value, event.currentTarget.selectionStart))}
      onKeyDown={(event) => {
        if (event.nativeEvent.isComposing) return;
        if (trigger && event.key === 'Escape') { event.preventDefault(); setTrigger(null); }
        else if (trigger && candidates.length && ['ArrowDown', 'ArrowUp'].includes(event.key)) {
          event.preventDefault(); setActive((previous) => (previous + (event.key === 'ArrowDown' ? 1 : candidates.length - 1) + candidates.length) % candidates.length);
        } else if (trigger && active >= 0 && event.key === 'Enter') { event.preventDefault(); choose(candidates[active]); }
        else if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') { event.preventDefault(); submit(); }
      }} role="combobox" aria-autocomplete="list" aria-label="タスクコメント" aria-expanded={Boolean(trigger)}
      aria-controls={trigger && candidates.length ? listId : undefined}
      aria-activedescendant={trigger && candidates[active] ? `${listId}-${candidates[active].targetId}` : undefined} />
    {trigger && <div className="task-comment-composer__candidates">
      {candidates.length > 0 && <ul id={listId} role="listbox" aria-label="メンション候補">
        {candidates.map((candidate, index) => <li id={`${listId}-${candidate.targetId}`} key={candidate.targetId} role="option" aria-selected={active === index}>
          <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => choose(candidate)} onMouseEnter={() => setActive(index)}>
            <PersonAvatar role="admin" name={candidate.label} person={{ profile_picture: candidate.profilePicture }} size={24} />
            {candidate.label}
          </button>
        </li>)}
      </ul>}
      {loading ? <span role="status">候補を読み込み中…</span> : error ? <span role="status">{error} <DsButton onClick={() => setRetry((v) => v + 1)}>再試行</DsButton></span> : !candidates.length ? <span>候補がありません。</span> : null}
      {cursor != null && !loading && <DsButton onClick={loadMore}>候補をもっと見る</DsButton>}
    </div>}
    <DsButton variant="secondary" size="sm" onClick={submit} disabled={disabled || readOnly || !value.trim()}>{disabled ? '投稿中...' : 'コメント投稿'}</DsButton>
  </div>;
}
